import { useCallback, useState } from 'react';

export interface SectionEditState {
    isEditing: boolean;
    isConfirmingDiscard: boolean;
    enter: () => void;
    /** Attempts to leave edit mode; opens the discard prompt when dirty. */
    requestExit: (isDirty: boolean) => void;
    confirmDiscard: () => void;
    cancelDiscard: () => void;
    /** Closes edit mode without prompting (use after a successful save). */
    closeAfterSave: () => void;
}

/**
 * Tracks per-section view↔edit state with a dirty-aware cancel flow.
 * The caller owns the dirty bit so we don't couple to any form library.
 */
export const useSectionEditState = (initial = false): SectionEditState => {
    const [isEditing, setIsEditing] = useState(initial);
    const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);

    const enter = useCallback(() => setIsEditing(true), []);

    const requestExit = useCallback((isDirty: boolean) => {
        if (isDirty) {
            setIsConfirmingDiscard(true);
        } else {
            setIsEditing(false);
        }
    }, []);

    const confirmDiscard = useCallback(() => {
        setIsConfirmingDiscard(false);
        setIsEditing(false);
    }, []);

    const cancelDiscard = useCallback(() => setIsConfirmingDiscard(false), []);

    const closeAfterSave = useCallback(() => {
        setIsConfirmingDiscard(false);
        setIsEditing(false);
    }, []);

    return {
        isEditing,
        isConfirmingDiscard,
        enter,
        requestExit,
        confirmDiscard,
        cancelDiscard,
        closeAfterSave,
    };
};
